import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Tag, User } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../App';

const BlogListPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blogs/?published=true&limit=50`)
      .then(r => r.json())
      .then(setBlogs)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="blog-list-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <Link to="/" className="inline-flex items-center text-gray-500 hover:text-white mb-8 transition-colors text-sm">
          <ArrowLeft className="mr-2" size={16} /> Inicio
        </Link>
        <motion.h1
          className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
          style={{ fontFamily: 'Playfair Display, serif' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Blog <span className="text-[#D4AF37]">LD Holdings</span>
        </motion.h1>
        <p className="text-gray-400 mb-12 text-base sm:text-lg">Educación financiera, tributaria y legal para tu vida.</p>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : blogs.length === 0 ? (
          <p className="text-gray-500 text-center py-20">Próximamente publicaremos contenido.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blogs.map((blog, i) => (
              <motion.div
                key={blog.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <Link
                  to={`/blog/${blog.slug || blog.id}`}
                  className="block group rounded-2xl border border-[#1a2332] overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-500"
                  data-testid={`blog-card-${blog.slug}`}
                >
                  {blog.cover_image && (
                    <div className="aspect-[16/9] overflow-hidden">
                      <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                  )}
                  <div className="p-5">
                    <h2 className="text-lg font-bold text-white group-hover:text-[#D4AF37] transition-colors mb-2">{blog.title}</h2>
                    {blog.excerpt && <p className="text-gray-400 text-sm leading-relaxed mb-3 line-clamp-2">{blog.excerpt}</p>}
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      {blog.author && <span className="flex items-center gap-1"><User size={12} /> {blog.author}</span>}
                      {blog.created_at && <span className="flex items-center gap-1"><Calendar size={12} /> {new Date(blog.created_at).toLocaleDateString('es-CO')}</span>}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blogs/${slug}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setBlog)
      .catch(() => setBlog(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!blog) return (
    <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-4">Blog no encontrado</h2>
        <Link to="/blog" className="text-[#D4AF37] hover:underline">Ver todos los blogs</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="blog-detail-page">
      {/* Hero */}
      {blog.cover_image && (
        <div className="relative h-[40vh] sm:h-[50vh] overflow-hidden">
          <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0e1a] via-[#0a0e1a]/50 to-transparent" />
        </div>
      )}

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 sm:pb-24" style={{ marginTop: blog.cover_image ? '-80px' : '80px' }}>
        <div className="relative z-10">
          <Link to="/blog" className="inline-flex items-center text-gray-500 hover:text-white mb-6 transition-colors text-sm">
            <ArrowLeft className="mr-2" size={16} /> Todos los blogs
          </Link>

          <motion.h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {blog.title}
          </motion.h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 mb-8 pb-8 border-b border-[#1a2332]">
            {blog.author && <span className="flex items-center gap-1.5"><User size={14} /> {blog.author}</span>}
            {blog.created_at && <span className="flex items-center gap-1.5"><Calendar size={14} /> {new Date(blog.created_at).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}</span>}
            {blog.tags?.map(t => <span key={t} className="flex items-center gap-1 text-[#D4AF37]/70"><Tag size={12} /> {t}</span>)}
          </div>

          {/* Blog Content */}
          <article
            className="prose prose-invert prose-lg max-w-none
              prose-headings:font-bold prose-headings:text-white
              prose-h2:text-xl prose-h2:sm:text-2xl prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3
              prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
              prose-li:text-gray-300
              prose-strong:text-white
              prose-a:text-[#D4AF37] prose-a:no-underline hover:prose-a:underline"
            dangerouslySetInnerHTML={{ __html: blog.content }}
            data-testid="blog-content"
          />

          {/* CTA at bottom */}
          <div className="mt-16 p-8 rounded-2xl border border-[#1a2332] text-center" style={{ background: 'linear-gradient(135deg, rgba(59,95,224,0.08), transparent)' }}>
            <h3 className="text-xl font-bold mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>¿Necesitas ayuda con tus impuestos?</h3>
            <p className="text-gray-400 text-sm mb-6">Nuestro equipo está listo para acompañarte.</p>
            <a href="https://wa.me/573208394000?text=Hola%2C%20vi%20su%20blog%20y%20me%20interesa%20asesor%C3%ADa%20tributaria" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#3B5FE0] text-white px-8 py-3 rounded-full font-bold hover:bg-[#3050c0] transition-all">
              Contactar Asesor
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export { BlogListPage, BlogDetailPage };
